import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import Intercom, { showSpace } from '@intercom/messenger-js-sdk';
import { useTheme } from '@mui/material/styles';
import { AppBar, Box, Toolbar, useMediaQuery } from '@mui/material';

import {
    ApiProvider,
    UserSubscription,
    useUser,
    useManifest,
} from '@travelity/api';
import { LoadingOverlay, useMediaDevice } from '@travelity/ui';
import DialogContent from '@mui/material/DialogContent';
import Dialog from '@mui/material/Dialog';

import Header from './components/header';
import Sidebar from './components/sidebar';

import LogoHeader from './components/logo-header';
import { RegisterSteps } from '../../views/register-steps';
import UserProvider from '../../contexts/user/user-provider';
import { useLocalStorage } from '../../hooks';

const drawerWidth = 224;

type ContextType = {
    mainIsLoading: boolean;
    setMainIsLoading: (v: boolean) => void;
};

function MainLayout() {
    const theme = useTheme();
    const defaultDrawerState = useMediaQuery('(min-width: 1450px)');
    // Handle left drawer
    const [leftDrawerOpened, setLeftDrawerOpened] = useState<boolean>();
    const showOpened =
        leftDrawerOpened !== undefined ? leftDrawerOpened : defaultDrawerState;
    const handleLeftDrawerToggle = useCallback(() => {
        setLeftDrawerOpened(
            prevState =>
                !(prevState !== undefined ? prevState : defaultDrawerState)
        );
    }, [defaultDrawerState]);

    const [mainIsLoading, setMainIsLoading] = useState(false);
    const context = useMemo(
        () => ({ mainIsLoading, setMainIsLoading }),
        [mainIsLoading]
    );

    useManifest();
    const { data: user, isLoading: isUserLoading, error } = useUser();

    useEffect(() => {
        if (user?.id) {
            window.posthog?.identify?.(user.id, {
                email: user.email,
                name: user.fullName,
            });
        }
    }, [user]);

    const profileIsComplete = !!user;
    const [superUser, setSuperUser] = useState(false);
    const contextUser = useMemo(
        () =>
            user
                ? {
                      ...user,
                      superUser,
                  }
                : undefined,
        [user, superUser]
    );

    // @ts-ignore
    const needsRegistration = !isUserLoading && error?.status === 409;
    // @ts-ignore
    const hasError = !isUserLoading && error?.status !== 409;
    const { isMobileTablet } = useMediaDevice();

    const [firstTime, setFirstTime] = useLocalStorage(
        `${user?.id}_firstTime_intercom`,
        'true'
    );
    const intercomFirstTime = useRef(firstTime);
    intercomFirstTime.current = firstTime;

    const intercomRun = useRef(false);
    useEffect(() => {
        if (user && !intercomRun.current) {
            const companies = user.workspaces.list.map(workspace => ({
                company_id: workspace.id,
                name: workspace.individual
                    ? 'Personal workspace'
                    : `${workspace.name} (${workspace.role})`,
                plan: (user.subscription as UserSubscription)?.name,
            }));

            Intercom({
                app_id: 'xmobvz3r',
                user_id: user.id,
                name: user.fullName,
                email: user.email,
                created_at: user.createdAt,
                company: companies,
            });
            intercomRun.current = true;
            setTimeout(() => {
                if (intercomFirstTime.current === 'true') {
                    showSpace('home');
                    setFirstTime('false');
                }
            }, 3000);
        }
    }, [user]);

    return (
        <Box sx={{ display: 'flex' }}>
            <ApiProvider superUser={superUser}>
                {/* header */}
                <AppBar
                    enableColorOnDark
                    position="fixed"
                    color="inherit"
                    className="hide-on-print"
                    elevation={0}
                    sx={{
                        borderRadius: 0,
                        borderBottom: '1px solid #E7E8F2',
                        bgcolor: '#FFFFFF',
                    }}
                >
                    {!isUserLoading && (
                        <Toolbar sx={{ '&&&': { pl: 1 } }} variant="dense">
                            {profileIsComplete ? (
                                <Header
                                    handleLeftDrawerToggle={
                                        handleLeftDrawerToggle
                                    }
                                    user={contextUser}
                                    setSuperUser={setSuperUser}
                                />
                            ) : (
                                <LogoHeader />
                            )}
                        </Toolbar>
                    )}
                </AppBar>

                {profileIsComplete ? (
                    <>
                        <Sidebar
                            user={contextUser}
                            drawerOpen={showOpened}
                            drawerToggle={handleLeftDrawerToggle}
                        />

                        <Box
                            component="main"
                            sx={{
                                height: 'calc(100vh - 60px)',
                                bgcolor: '#F4F6FA',
                                mt: '60px',
                                overflow: 'hidden',
                                ...theme.typography.body1,
                                ...(!showOpened || isMobileTablet
                                    ? {
                                          borderBottomLeftRadius: 0,
                                          borderBottomRightRadius: 0,
                                          width: 'calc(100% + 16px)',
                                          transition: theme.transitions.create(
                                              'margin',
                                              {
                                                  easing: theme.transitions
                                                      .easing.sharp,
                                                  duration:
                                                      theme.transitions.duration
                                                          .leavingScreen,
                                              }
                                          ),
                                          marginLeft: `${-drawerWidth - 16}px`,
                                          [theme.breakpoints.down('md')]: {
                                              marginLeft: '-16px',
                                          },
                                      }
                                    : {
                                          transition: theme.transitions.create(
                                              'margin',
                                              {
                                                  easing: theme.transitions
                                                      .easing.easeOut,
                                                  duration:
                                                      theme.transitions.duration
                                                          .enteringScreen,
                                              }
                                          ),
                                          marginLeft: `0`,
                                          borderBottomLeftRadius: 0,
                                          borderBottomRightRadius: 0,
                                          width: 'calc(100% + 16px)',
                                      }),
                            }}
                        >
                            <UserProvider user={contextUser}>
                                <Outlet context={context} />
                                {context.mainIsLoading && (
                                    <LoadingOverlay
                                        title="Please wait..."
                                        subTitle="It’ll just take a moment."
                                    />
                                )}
                            </UserProvider>
                        </Box>
                    </>
                ) : needsRegistration ? (
                    <Box
                        component="main"
                        sx={{
                            height: 'calc(100vh - 60px)',
                            bgcolor: '#F4F6FA',
                            mt: '60px',
                            width: 1,
                            overflow: 'hidden',
                            ...theme.typography.body1,
                        }}
                    >
                        <RegisterSteps user={contextUser} />
                    </Box>
                ) : hasError ? (
                    <Dialog open>
                        <DialogContent>
                            Something went wrong. Travelity team will resolve
                            this soon.
                            <br />
                            Please try again in few minutes
                        </DialogContent>
                    </Dialog>
                ) : (
                    <LoadingOverlay
                        title="Preparing your environment."
                        subTitle="It’ll just take a moment."
                    />
                )}
            </ApiProvider>
        </Box>
    );
}

export function useLayoutContext() {
    return useOutletContext<ContextType>();
}

export default MainLayout;
